import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatIDR } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";

const REPORTS = [
  { key: "lpd-infaq", label: "LPD Infaq", title: "Laporan Perubahan Dana Infaq/Sedekah/DSKL" },
  { key: "lpd-zakat", label: "LPD Zakat", title: "Laporan Perubahan Dana Zakat" },
  { key: "lak", label: "LAK", title: "Laporan Arus Kas" },
];

function Row({ kind, label, amount }) {
  if (kind === "section") {
    return (
      <tr className="bg-emerald-50">
        <td colSpan={2} className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-emerald-900">{label}</td>
      </tr>
    );
  }
  const base = "px-4 py-2 border-b border-slate-100";
  const isBold = kind === "total" || kind === "grand";
  return (
    <tr className={kind === "grand" ? "bg-slate-50" : ""}>
      <td className={`${base} ${isBold ? "font-bold text-slate-900" : "text-slate-600 pl-8"} text-sm`}>{label}</td>
      <td className={`${base} text-right text-sm whitespace-nowrap ${isBold ? "font-bold" : ""} ${
        kind === "grand" ? "text-emerald-800" : "text-slate-800"
      }`}>
        {amount === null || amount === undefined ? "" : formatIDR(amount)}
      </td>
    </tr>
  );
}

function buildRows(report, data) {
  if (!data) return [];
  if (report === "lak") {
    if (!data.infaq || !data.zakat || !data.investasi) return [];
    const rows = [{ kind: "section", label: "RINGKASAN LPD INFAQ/SEDEKAH/DSKL" }];
    rows.push({ kind: "item", label: "Total Penerimaan Infaq", amount: data.infaq.total_penerimaan });
    rows.push({ kind: "item", label: "Total Pentasyarufan Infaq", amount: -data.infaq.total_pentasyarufan });
    rows.push({ kind: "total", label: "Kenaikan (Penurunan) Dana Infaq", amount: data.infaq.surplus });
    rows.push({ kind: "section", label: "RINGKASAN LPD ZAKAT" });
    rows.push({ kind: "item", label: "Total Penerimaan Zakat", amount: data.zakat.total_penerimaan });
    rows.push({ kind: "item", label: "Total Pentasyarufan Zakat", amount: -data.zakat.total_pentasyarufan });
    rows.push({ kind: "total", label: "Kenaikan (Penurunan) Dana Zakat", amount: data.zakat.surplus });
    rows.push({ kind: "section", label: "AKTIVITAS INVESTASI" });
    data.investasi.items.forEach((it) => rows.push({ kind: "item", label: `Perolehan Aset - ${it.label}`, amount: it.amount }));
    rows.push({ kind: "total", label: "Kas Netto dari Aktivitas Investasi", amount: data.investasi.total });
    rows.push({ kind: "total", label: "Kenaikan (Penurunan) Kas", amount: data.kenaikan_kas });
    rows.push({ kind: "grand", label: "KAS AWAL PERIODE", amount: data.kas_awal });
    rows.push({ kind: "grand", label: "KAS AKHIR PERIODE", amount: data.kas_akhir });
    return rows;
  }
  if (!data.penerimaan || !data.pentasyarufan) return [];
  const rows = [{ kind: "grand", label: "SALDO AWAL DANA", amount: data.saldo_awal }];
  rows.push({ kind: "section", label: "PENERIMAAN" });
  data.penerimaan.forEach((it) => rows.push({ kind: "item", label: it.label, amount: it.amount }));
  rows.push({ kind: "total", label: "Total Penerimaan", amount: data.total_penerimaan });
  rows.push({ kind: "section", label: "PENTASYARUFAN" });
  data.pentasyarufan.forEach((it) => rows.push({ kind: "item", label: it.label, amount: it.amount }));
  rows.push({ kind: "total", label: "Total Pentasyarufan", amount: data.total_pentasyarufan });
  rows.push({ kind: "total", label: "Kenaikan (Penurunan) Dana", amount: data.surplus });
  rows.push({ kind: "grand", label: "SALDO AKHIR DANA", amount: data.saldo_akhir });
  return rows;
}

export default function Laporan() {
  const { user } = useAuth();
  const canPickOperator = ["admin", "pengurus"].includes(user?.role);
  const year = new Date().getFullYear();
  const [tab, setTab] = useState("lpd-infaq");
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [operators, setOperators] = useState([]);
  const [selectedUser, setSelectedUser] = useState("semua");
  const canEditOpening = ["admin", "operator"].includes(user?.role);
  const [opening, setOpening] = useState({ infaq: 0, zakat: 0 });
  const [savingOpening, setSavingOpening] = useState(false);

  useEffect(() => {
    api.get("/settings/opening-balance").then((r) => setOpening(r.data)).catch(() => {});
  }, []);

  const saveOpening = async () => {
    setSavingOpening(true);
    try {
      const { data: saved } = await api.put("/settings/opening-balance", { infaq: Number(opening.infaq) || 0, zakat: Number(opening.zakat) || 0 });
      setOpening(saved);
      toast.success("Saldo dana awal tersimpan");
      fetchReport();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingOpening(false);
    }
  };

  useEffect(() => {
    if (canPickOperator) {
      api.get("/users/operators").then((r) => setOperators(r.data)).catch(() => {});
    }
  }, [canPickOperator]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const url = tab === "lak" ? "/reports/lak" : "/reports/lpd";
      const params = tab === "lak"
        ? { start, end }
        : { fund: tab === "lpd-infaq" ? "infaq" : "zakat", start, end };
      if (selectedUser !== "semua") params.user_id = selectedUser;
      const { data: res } = await api.get(url, { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, start, end, selectedUser]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = { report: tab, format, start, end };
      if (selectedUser !== "semua") params.user_id = selectedUser;
      const res = await api.get("/reports/export", {
        params,
        responseType: "blob",
      });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-${tab}-${start}-${end}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Laporan ${format.toUpperCase()} berhasil diunduh`);
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    } finally {
      setExporting("");
    }
  };

  const activeReport = REPORTS.find((r) => r.key === tab);
  const rows = buildRows(tab, data);

  return (
    <div className="space-y-6" data-testid="laporan-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Laporan Keuangan</h1>
        <p className="text-sm text-slate-500 mt-1">LPD Infaq, LPD Zakat, dan Laporan Arus Kas sesuai PSAK 109</p>
        {user?.role === "operator" && (
          <p className="mt-2 inline-block rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-800" data-testid="laporan-scope-note">
            Menampilkan data yang Anda input
          </p>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Dari Tanggal</Label>
              <Input type="date" data-testid="report-start-input" value={start} onChange={(e) => setStart(e.target.value)} className="w-[170px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Sampai Tanggal</Label>
              <Input type="date" data-testid="report-end-input" value={end} onChange={(e) => setEnd(e.target.value)} className="w-[170px]" />
            </div>
            <Button
              data-testid="report-apply-button"
              onClick={fetchReport}
              className="bg-emerald-800 hover:bg-emerald-700 text-white"
            >
              Terapkan
            </Button>
            {canPickOperator && (
              <div className="space-y-1.5">
                <Label className="text-xs">Hasil Input Operator</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-[190px]" data-testid="report-operator-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semua" data-testid="report-operator-semua">Semua Data</SelectItem>
                    {operators.map((o) => (
                      <SelectItem key={o.user_id} value={o.user_id} data-testid={`report-operator-${o.user_id}`}>
                        {o.name} ({o.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2 md:ml-auto">
              <Button
                variant="outline"
                data-testid="export-pdf-button"
                onClick={() => handleExport("pdf")}
                disabled={!!exporting}
              >
                {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4 text-red-600" />}
                Export PDF
              </Button>
              <Button
                variant="outline"
                data-testid="export-excel-button"
                onClick={() => handleExport("xlsx")}
                disabled={!!exporting}
              >
                {exporting === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-700" />}
                Export Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canEditOpening && (
        <Card data-testid="opening-balance-card">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Saldo Dana Awal — Infaq/Sedekah/DSKL</Label>
                <Input type="number" data-testid="opening-infaq-input" value={opening.infaq} onChange={(e) => setOpening({ ...opening, infaq: e.target.value })} className="w-[220px]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Saldo Dana Awal — Zakat</Label>
                <Input type="number" data-testid="opening-zakat-input" value={opening.zakat} onChange={(e) => setOpening({ ...opening, zakat: e.target.value })} className="w-[220px]" />
              </div>
              <Button data-testid="opening-save-button" onClick={saveOpening} disabled={savingOpening} className="bg-emerald-800 hover:bg-emerald-700 text-white">
                {savingOpening ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Saldo Awal"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="report-tabs">
          {REPORTS.map((r) => (
            <TabsTrigger key={r.key} value={r.key} data-testid={`report-tab-${r.key}`}>{r.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card data-testid="report-content">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg text-center">{activeReport.title}</CardTitle>
          <p className="text-center text-xs text-slate-500">Periode {start} s.d. {end} · LAZISNU Dawe · PSAK 109</p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16" data-testid="report-loading">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-800" />
            </div>
          ) : (
            <table className="w-full" data-testid="report-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Uraian</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <Row key={i} kind={r.kind} label={r.label} amount={r.amount} />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
